import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '../../utils/cn';
import {
  buildAirportLabel,
  searchAirports,
  type AirportSuggestion,
} from '../../services/airportSearchService';

const MIN_KEYWORD_LENGTH = 2;
const DEBOUNCE_MS = 250;

interface AirportPickerProps {
  label?: string;
  value: string;
  onSelect: (airport: AirportSuggestion) => void;
  placeholder?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
}

export function suggestionLabel(airport: AirportSuggestion): string {
  return airport.displayLabel || buildAirportLabel(airport);
}

/**
 * Searchable airport combobox. `value` is the stored IATA code, which is shown
 * in the field until the admin types a new search.
 */
export function AirportPicker({
  label,
  value,
  onSelect,
  placeholder,
  required,
  error,
  helperText,
}: AirportPickerProps) {
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const containerRef = useRef<HTMLDivElement>(null);

  const [keyword, setKeyword] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState<AirportSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Until the admin searches, the field mirrors the stored code.
  const inputValue = isSearching ? keyword : value;

  useEffect(() => {
    if (!isSearching || keyword.trim().length < MIN_KEYWORD_LENGTH) {
      setOptions([]);
      setActiveIndex(-1);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      searchAirports(keyword)
        .then(results => {
          if (cancelled) return;
          setOptions(results);
          setActiveIndex(results.length > 0 ? 0 : -1);
          setSearchError(null);
          setIsOpen(true);
        })
        .catch((cause: unknown) => {
          if (cancelled) return;
          setOptions([]);
          setActiveIndex(-1);
          setSearchError(
            cause instanceof Error ? cause.message : 'Failed to search airports'
          );
          setIsOpen(true);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [keyword, isSearching]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  const commitSelection = (airport: AirportSuggestion) => {
    onSelect(airport);
    setIsSearching(false);
    setKeyword('');
    setOptions([]);
    setActiveIndex(-1);
    setIsOpen(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (options.length === 0) return;
      setIsOpen(true);
      setActiveIndex(current => (current + 1) % options.length);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (options.length === 0) return;
      setIsOpen(true);
      setActiveIndex(
        current => (current - 1 + options.length) % options.length
      );
      return;
    }
    if (event.key === 'Enter') {
      if (isOpen && activeIndex >= 0 && options[activeIndex]) {
        event.preventDefault();
        commitSelection(options[activeIndex]);
      }
      return;
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  const showNoResults =
    isOpen &&
    !searchError &&
    options.length === 0 &&
    isSearching &&
    keyword.trim().length >= MIN_KEYWORD_LENGTH;

  return (
    <div className="w-full" ref={containerRef}>
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 dark:text-[var(--amy-text)] mb-1"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            isOpen && activeIndex >= 0
              ? `${listboxId}-option-${activeIndex}`
              : undefined
          }
          autoComplete="off"
          required={required}
          placeholder={placeholder}
          value={inputValue}
          onChange={event => {
            setIsSearching(true);
            setKeyword(event.target.value);
            setSearchError(null);
          }}
          onFocus={() => {
            if (options.length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className={cn(
            'block w-full rounded-xl border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-500 dark:bg-[var(--amy-field)] dark:border-[var(--amy-border)] dark:text-[var(--amy-text)] dark:placeholder-[var(--amy-muted)]',
            'focus:outline-none focus:ring-2 focus:ring-[var(--amy-accent)] focus:border-transparent',
            error && 'border-red-500 focus:ring-red-500'
          )}
        />
        {isOpen && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Airport results"
            className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-[var(--amy-border)] dark:bg-[var(--amy-card)]"
          >
            {searchError && (
              <li className="px-3 py-2 text-sm text-red-600 dark:text-red-400">
                {searchError}
              </li>
            )}
            {showNoResults && (
              <li className="px-3 py-2 text-sm text-gray-500 dark:text-[var(--amy-muted)]">
                No airports found
              </li>
            )}
            {options.map((airport, index) => (
              // Keyboard selection lives on the combobox input via
              // aria-activedescendant, which is the ARIA listbox pattern.
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={airport.iataCode}
                id={`${listboxId}-option-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={event => event.preventDefault()}
                onClick={() => commitSelection(airport)}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'cursor-pointer px-3 py-2',
                  index === activeIndex &&
                    'bg-gray-100 dark:bg-[var(--amy-field)]'
                )}
              >
                <div className="text-sm text-gray-900 dark:text-[var(--amy-text)]">
                  {suggestionLabel(airport)}
                </div>
                <div className="text-xs text-gray-500 dark:text-[var(--amy-muted)]">
                  {airport.countryName || airport.countryCode}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helperText && !error && (
        <p className="mt-1 text-sm text-gray-700 dark:text-[var(--amy-muted)]">
          {helperText}
        </p>
      )}
    </div>
  );
}
